import { useState } from 'react'
import { useWallet } from '../contexts/WalletContext'
import { useToast } from '../contexts/ToastContext'

interface Match {
  matchId: string
  sideA: string
  sideB: string
  totalPool: string
}

interface CreateDuelModalProps {
  match: Match
  onClose: () => void
  onSubmit: (side: 'sideA' | 'sideB', amount: string) => void
}

export default function CreateDuelModal({ match, onClose, onSubmit }: CreateDuelModalProps) {
  const [selectedSide, setSelectedSide] = useState<'sideA' | 'sideB'>('sideA')
  const [amount, setAmount] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  const { balance } = useWallet()
  const toast = useToast()

  const minBet = 0.01
  const maxBet = 10000
  const availableBalance = balance ? parseFloat(balance) : 0

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    const amountNum = parseFloat(amount)
    
    // Validation
    if (!amount || isNaN(amountNum)) {
      toast.error('Please enter a valid amount')
      return
    }
    
    if (amountNum < minBet) {
      toast.warning(`Minimum predict amount is ${minBet} CHNX`)
      return
    }
    
    if (amountNum > maxBet) {
      toast.warning(`Maximum predict amount is ${maxBet} CHNX`)
      return
    }

    // Check wallet balance
    if (amountNum > availableBalance) {
      toast.error(`Insufficient balance! Your available balance is ${availableBalance.toFixed(2)} CHNX`)
      return
    }

    setIsSubmitting(true)
    try {
      await onSubmit(selectedSide, amount)
      toast.success('Prediction created successfully! Waiting for opponent')
      onClose()
    } catch (error: any) {
      toast.error(error.message || 'Failed to create prediction, please try again')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleQuickAmount = (value: number) => {
    setAmount(value.toString())
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[100]">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Create Duel</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Side Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Select Your Side
            </label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setSelectedSide('sideA')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  selectedSide === 'sideA'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <div className="font-semibold">{match.sideA}</div>
                {selectedSide === 'sideA' && (
                  <div className="text-sm mt-1">✓ Selected</div>
                )}
              </button>
              
              <button
                type="button"
                onClick={() => setSelectedSide('sideB')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  selectedSide === 'sideB'
                    ? 'border-red-500 bg-red-50 text-red-700'
                    : 'border-gray-200 hover:border-red-300'
                }`}
              >
                <div className="font-semibold">{match.sideB}</div>
                {selectedSide === 'sideB' && (
                  <div className="text-sm mt-1">✓ Selected</div>
                )}
              </button>
            </div>
          </div>

          {/* Amount Input */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Bet Amount (CHNX)
              </label>
              <span className="text-sm text-gray-600">
                Available Balance: <span className="font-semibold text-green-600">{availableBalance.toFixed(3)} CHNX</span>
              </span>
            </div>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={`${minBet} - ${maxBet}`}
              step="0.00000001"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <div className="flex justify-between items-center mt-1">
              <p className="text-xs text-gray-500">
                Min: {minBet} CHNX | Max: {maxBet} CHNX
              </p>
              <button
                type="button"
                onClick={() => setAmount(availableBalance.toString())}
                className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                disabled={availableBalance <= 0}
              >
                All
              </button>
            </div>
          </div>

          {/* Quick Amount Buttons */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quick Select
            </label>
            <div className="grid grid-cols-4 gap-2">
              {[50, 100, 500, 1000].map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => handleQuickAmount(value)}
                  className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-sm font-medium transition-colors"
                >
                  {value}
                </button>
              ))}
            </div>
          </div>

          {/* Summary */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600">Predicting:</span>
              <span className="font-semibold text-gray-900">
                {selectedSide === 'sideA' ? match.sideA : match.sideB}
              </span>
            </div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600">Your bet:</span>
              <span className="font-semibold text-gray-900">{amount || '0'} CHNX</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Potential win:</span>
              <span className="font-semibold text-green-600">
                {amount ? (parseFloat(amount) * 2).toFixed(3) : '0'} CHNX
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:bg-gray-400"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Duel'}
            </button>
          </div>
        </form>

        {/* Info */}
        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <p className="text-xs text-blue-800 mb-2">
            💡 <strong>How it works:</strong> Your CHNX will be locked. When someone matches your duel, 
            you both compete on the match result. Winner takes all!
          </p>
          <p className="text-xs text-blue-600">
            📌 <strong>Next step:</strong> You will be asked to confirm the transfer to Escrow before creating the prediction.
          </p>
        </div>
      </div>
    </div>
  )
}

